// shared-components/components/DigitalLiteracyClasses.jsx
import React from 'react';

/**
 * DigitalLiteracyClasses - shared, presentational list of digital literacy courses.
 *
 * Pure/presentational so BOTH the on-device helper (Electron) and the web assistant
 * can reuse it. It does NOT open links itself — link handling is delegated to the
 * `onOpenCourse` callback so each platform can use its own mechanism
 * (Electron: window.electronAPI.openExternal; web: window.open / <a>).
 *
 * @param {Array}    courses     - [{ id, name, url }] (url already resolved to the right language)
 * @param {object}   achievery   - { name, description, url } partner library (optional)
 * @param {string}   overviewUrl - link to the full classes page on compudopt.org (optional)
 * @param {string}   language    - 'en' | 'es' (drives the generic chrome copy)
 * @param {Function} onOpenCourse- (url) => void, called when a course/link is selected
 * @param {boolean}  showIntro   - show the short intro line (default true)
 */

const COPY = {
  en: {
    intro: 'Free video courses to build your computer skills — watch anytime, at your own pace.',
    watch: 'Watch',
    achieveryDesc: 'Hundreds of free learning videos from AT&T Connected Learning.',
    overview: 'See all classes on compudopt.org',
    empty: 'Classes are loading. Please check back in a moment.',
  },
  es: {
    intro: 'Cursos gratuitos en video para mejorar tus habilidades — míralos cuando quieras, a tu ritmo.',
    watch: 'Ver',
    achieveryDesc: 'Cientos de videos de aprendizaje gratuitos de AT&T Connected Learning.',
    overview: 'Ver todas las clases en compudopt.org',
    empty: 'Las clases se están cargando. Vuelve a intentarlo en un momento.',
  },
};

function ClassRow({ icon, title, subtitle, actionLabel, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border-default)',
        borderRadius: 'var(--radius-md, 12px)',
        padding: 'var(--space-3) var(--space-4)',
        cursor: 'pointer',
        textAlign: 'left',
        transition: 'all var(--duration-fast, 0.15s) var(--ease-out, ease)',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'var(--info-bg)';
        e.currentTarget.style.borderColor = 'var(--compudopt-blue)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'var(--bg-secondary)';
        e.currentTarget.style.borderColor = 'var(--border-default)';
      }}
    >
      <span style={{ fontSize: '22px', flexShrink: 0, lineHeight: 1 }}>{icon}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{
          display: 'block',
          fontSize: 'var(--text-sm, 14px)',
          fontWeight: 'var(--font-semibold, 600)',
          color: 'var(--text-primary)',
        }}>
          {title}
        </span>
        {subtitle && (
          <span style={{
            display: 'block',
            fontSize: 'var(--text-xs, 12px)',
            color: 'var(--text-secondary)',
            marginTop: '2px',
          }}>
            {subtitle}
          </span>
        )}
      </span>
      <span style={{
        flexShrink: 0,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        background: 'var(--compudopt-blue)',
        color: 'var(--text-on-primary, #fff)',
        fontSize: 'var(--text-xs, 12px)',
        fontWeight: 'var(--font-semibold, 600)',
        padding: '6px 12px',
        borderRadius: 'var(--radius-pill, 25px)',
      }}>
        ▶ {actionLabel}
      </span>
    </button>
  );
}

export default function DigitalLiteracyClasses({
  courses = [],
  achievery = null,
  overviewUrl = null,
  language = 'en',
  onOpenCourse,
  showIntro = true,
}) {
  const t = COPY[language] || COPY.en;
  const open = (url) => { if (url && typeof onOpenCourse === 'function') onOpenCourse(url); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {showIntro && (
        <p style={{
          margin: 0,
          fontSize: 'var(--text-sm, 14px)',
          color: 'var(--text-secondary)',
          lineHeight: 'var(--leading-normal, 1.5)',
        }}>
          {t.intro}
        </p>
      )}

      {courses.length === 0 ? (
        <p style={{ margin: 0, fontSize: 'var(--text-sm, 14px)', color: 'var(--text-tertiary)' }}>
          {t.empty}
        </p>
      ) : (
        courses.map((course) => (
          <ClassRow
            key={course.id || course.name}
            icon="🎬"
            title={course.name}
            actionLabel={t.watch}
            onClick={() => open(course.url)}
          />
        ))
      )}

      {achievery && achievery.url && (
        <ClassRow
          icon="🌟"
          title={achievery.name || 'Achievery'}
          subtitle={t.achieveryDesc}
          actionLabel={t.watch}
          onClick={() => open(achievery.url)}
        />
      )}

      {overviewUrl && (
        <button
          onClick={() => open(overviewUrl)}
          style={{
            alignSelf: 'flex-start',
            marginTop: 'var(--space-1)',
            background: 'transparent',
            border: 'none',
            color: 'var(--compudopt-blue)',
            fontSize: 'var(--text-sm, 14px)',
            fontWeight: 'var(--font-semibold, 600)',
            cursor: 'pointer',
            padding: 'var(--space-1) 0',
          }}
        >
          {t.overview} &#8594;
        </button>
      )}
    </div>
  );
}
